import { useSyncExternalStore } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import BugReportDialog, { type BugReportDraft, type PreparedDraft, type QuestionSeed } from './BugReportDialog';
import BugReportSettings from './BugReportSettings';
import { bugApi, type BugConfig } from './bugApi';
import { markJuiRoot } from './jui';
import { nextPaint } from './capture';
import { bugReportOffered, config, hostOwnState, launcherConfig, onConfigChange, setProjectFeatures, uxWatchOffered } from './config';
import HelpLauncher from './help/HelpLauncher';
import { syncScreenMask } from './screenMask';
import { BUG_OWN_ATTR } from './recorder';
import { cleanPageHtml, collectClientState, finishClientState, pageSource, pauseTask } from './snapshot';
import { knownVersion } from './known';
import { injectStyles } from './styles';
import UxSessions from './ux/UxSessions';
import UxWatchPill from './ux/UxWatchPill';
import WhatsNewScreen, { type WhatsNewScreenProps } from './whatsNew/WhatsNewScreen';
import { startUxWatch as startUxRecording, uxWatchActive } from './ux/uxWatch';

export interface ReadyInfo {
  enabled: boolean;
  isSuper: boolean;
  project: string | null;
}

/** What a host menu shows: "back to the report" while a draft is open, "recording" while the watch pill is up. */
export interface WidgetState {
  draft: boolean;
  minimized: boolean;
  uxWatching: boolean;
}

interface Store {
  draft: BugReportDraft | null;
  sent: boolean;
  minimized: boolean;
  ux: boolean;
  /** The service lets the user in (last `ready()` answer): the launcher's report items are live. */
  bugsOn: boolean;
  /** The service lets the user see the list (last `ready()` answer): the launcher's "Репорты и вопросы" item. */
  isSuper: boolean;
  /** Questions heading the open draft («Сделать репортом»); a new draft starts with its own. */
  questions: QuestionSeed[];
  /** Bumped on `configure()`: the overlay re-reads `launcher`. */
  configSeq: number;
}

let store: Store = { draft: null, sent: false, minimized: false, ux: false, bugsOn: false, isSuper: false, questions: [], configSeq: 0 };
let publicState: WidgetState = { draft: false, minimized: false, uxWatching: false };
const listeners = new Set<() => void>();
let draftSeq = 0;

function set(patch: Partial<Store>): void {
  store = { ...store, ...patch };
  const next: WidgetState = { draft: !!store.draft && !store.sent, minimized: store.minimized, uxWatching: store.ux };
  if (next.draft !== publicState.draft || next.minimized !== publicState.minimized || next.uxWatching !== publicState.uxWatching) publicState = next;
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      // A host listener must not break the widget.
    }
  }
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Same object until something changes: fits `useSyncExternalStore(jaioraBugs.subscribe, jaioraBugs.state)`. */
export const getState = (): WidgetState => publicState;

let configCache: BugConfig | null = null;
let readyCached: ReadyInfo | null = null;

interface ReadyRequest {
  promise: Promise<ReadyInfo>;
  /** Started by a refresh: a later refresh shares it; a plain one may predate the user change. */
  refresh: boolean;
}

let readyInflight: ReadyRequest | null = null;

export interface ReadyOptions {
  /** Ask the service again (the host's user changed without a reload); concurrent calls still share one request. */
  refresh?: boolean;
}

// Once per page load, and only where the site shows the help button: what it offers by its own code (features_screen).
let stateReported = false;
function reportState(): void {
  if (stateReported || !config().launcher) return;
  stateReported = true;
  bugApi.reportState(hostOwnState()).catch(() => {
    stateReported = false;
  });
}

const DISABLED: ReadyInfo = { enabled: false, isSuper: false, project: null };

function askConfig(refresh: boolean): ReadyRequest {
  const request: ReadyRequest = { refresh, promise: Promise.resolve(DISABLED) };
  // Only the latest request writes the cache: a superseded one answers its own callers and nothing else.
  const current = () => readyInflight === request;
  request.promise = bugApi
    .config()
    .then((cfg) => {
      const info = { enabled: true, isSuper: !!cfg.isSuper, project: typeof cfg.project === 'string' ? cfg.project : null };
      if (current()) {
        configCache = cfg;
        setProjectFeatures(cfg.features);
        reportState();
        readyCached = info;
        if (store.bugsOn !== info.enabled || store.isSuper !== info.isSuper) set({ bugsOn: info.enabled, isSuper: info.isSuper });
      }
      return info;
    })
    .catch(() => {
      if (current()) {
        configCache = null;
        readyCached = null;
        if (store.bugsOn || store.isSuper) set({ bugsOn: false, isSuper: false });
      }
      return DISABLED;
    })
    .finally(() => {
      if (current()) readyInflight = null;
    });
  return request;
}

/**
 * Сс1–Сс2 (Quint `service_recheck`): the service was down for a moment (a restart on deploy) — the last answer said «off» and the
 * help menu's report items stayed grey until a page reload. Asked again when the menu opens, the tab comes back or the network
 * returns; nothing is asked while the items are live or the host has no report items.
 */
export function recheckService(): void {
  if (store.bugsOn || launcherConfig()?.bugs === false || !launcherConfig()) return;
  void ready({ refresh: true });
}

/**
 * `GET <base>/bugs/api/config`, cached once it succeeds; any failure — disabled (and asked again next time).
 * `refresh` drops the cached answer, so plain calls made meanwhile wait for the new one instead of a stale `isSuper`.
 */
export function ready(options: ReadyOptions = {}): Promise<ReadyInfo> {
  if (options.refresh) {
    readyCached = null;
    if (!readyInflight?.refresh) readyInflight = askConfig(true);
    return readyInflight.promise;
  }
  if (readyCached) return Promise.resolve(readyCached);
  readyInflight ??= askConfig(false);
  return readyInflight.promise;
}

const userName = (cfg: BugConfig | null): string => {
  const user = cfg?.user;
  if (typeof user === 'string') return user;
  return user && typeof user.name === 'string' ? user.name : '';
};

let root: Root | null = null;
let container: HTMLDivElement | null = null;
/** A double click on the host menu must not take two snapshots. */
let opening: Promise<void> | null = null;

function startDraft(fromQuestions: QuestionSeed[] = []): Promise<void> {
  opening ??= newDraft(fromQuestions).finally(() => {
    opening = null;
  });
  return opening;
}

function Overlay() {
  const s = useSyncExternalStore(subscribe, () => store);
  const launcher = launcherConfig();
  return (
    <>
      {launcher && (
        <HelpLauncher
          launcher={launcher}
          bugsOn={s.bugsOn}
          isSuper={s.isSuper}
          draft={!!s.draft && !s.sent}
          minimized={s.minimized}
          uxWatching={s.ux}
          onBugReport={() => void open()}
          onReportFromQuestion={(seeds) => void openFromQuestions(seeds)}
          onUxWatch={startUxWatch}
          onMenuOpen={recheckService}
        />
      )}
      {s.ux && <UxWatchPill isSuper={!!configCache?.isSuper} onDone={() => set({ ux: false })} onOpen={openUxSessions} />}
      {s.draft && (
        <BugReportDialog
          key={s.draft.id}
          draft={s.draft}
          minimized={s.minimized}
          onMinimize={() => set({ minimized: true })}
          onRestore={() => set({ minimized: false })}
          onClose={() => set({ draft: null, sent: false, minimized: false, questions: [] })}
          onSent={() => set({ sent: true })}
          onAnother={() => void startDraft()}
          maxMb={configCache?.limits?.maxBodyMb || undefined}
          questions={s.questions}
        />
      )}
    </>
  );
}

/** The dialog, the area picker and the pill live in their own container: skipped by the recorder, the shot and the HTML. */
function ensureRoot(): void {
  // A host that rewrites <body> drops the container: put the same one back, its React tree is intact.
  if (container) {
    if (!container.isConnected) document.body.appendChild(container);
    return;
  }
  injectStyles();
  container = document.createElement('div');
  container.className = 'jaiora-bugs';
  container.setAttribute(BUG_OWN_ATTR, '');
  markJuiRoot(container);
  document.body.appendChild(container);
  root = createRoot(container);
  root.render(<Overlay />);
}

let configWatched = false;

/**
 * After `configure()`: a launcher config mounts the overlay and asks the service whether the user is let in; the overlay
 * re-renders for any change (launcher set, changed or removed on sign-out).
 */
export function watchConfig(): void {
  if (configWatched || typeof document === 'undefined') return;
  configWatched = true;
  let had = false;
  onConfigChange(() => {
    syncScreenMask();
    const launcher = launcherConfig();
    if (launcher) {
      ensureRoot();
      // A new sign-in (launcher back after null) must not inherit the previous user's answer.
      if (launcher.bugs !== false) void ready(had ? {} : { refresh: true });
    }
    had = !!launcher;
    set({ configSeq: store.configSeq + 1 });
  });
  const back = () => {
    if (document.visibilityState !== 'hidden') recheckService();
  };
  document.addEventListener('visibilitychange', back);
  window.addEventListener('online', recheckService);
}

function openUxSessions(): void {
  const open = config().onOpenUxSessions;
  if (open) open();
  else window.location.hash = '#ux-sessions';
}

/**
 * A new draft: snapshot of the page state and the page copy are taken after the host menu (or the previous panel) has left
 * the page, two frames later, and only the cheap part of it (report 74: the dictionary pass over the record and the page
 * HTML held the page 1-4 s). The panel opens and the page moves aside at once; nothing is shot at opening (Quint manual_shot
 * Sn1, report 287): the first shot is the person's, and the report cannot go without it. The cleaning (`prepare`) goes on
 * later in pieces and again at sending if the dictionary has grown since (P217).
 */
async function newDraft(fromQuestions: QuestionSeed[]): Promise<void> {
  ensureRoot();
  set({ draft: null, sent: false });
  const [, info] = await Promise.all([nextPaint(), ready()]);
  const clientState = collectClientState({ username: userName(configCache), isSuper: info.isSuper, app: safeAppState() }, { again: false });
  const source = pageSource();
  // Marked names are hidden on every shot (Quint screen_mask Mc3): no shield to turn it off.
  const masked = true;
  draftSeq += 1;
  set({
    draft: { id: draftSeq, masked, clientState, pageHtml: '', ...preparer(clientState, source), pageUrl: window.location.href },
    questions: fromQuestions,
    minimized: false,
  });
  await nextPaint();
}

/**
 * The cleaned state and page HTML of a draft, computed once per dictionary version. `fresh` (sending, the agent) — what
 * comes back is cleaned with the dictionary as it is at that moment: a name learned while it ran cleans again; the
 * background call takes one pass. `dispose` (the draft is gone) stops a pass at its next piece.
 */
function preparer(state: Record<string, unknown>, source: string): Pick<BugReportDraft, 'prepare' | 'dispose'> {
  let job: { version: number; done: Promise<PreparedDraft> } | null = null;
  let disposed = false;
  const pause = async () => {
    await pauseTask();
    if (disposed) throw new Error('черновик закрыт');
  };
  const run = async (): Promise<PreparedDraft> => ({ clientState: await finishClientState(state, pause), pageHtml: await cleanPageHtml(source, pause) });
  return {
    prepare: async ({ fresh = true } = {}) => {
      for (;;) {
        const version = knownVersion();
        if (!job || job.version !== version) job = { version, done: run() };
        const ready = await job.done;
        if (!fresh || knownVersion() === version) return ready;
      }
    },
    dispose: () => {
      disposed = true;
    },
  };
}

function safeAppState(): Record<string, unknown> | undefined {
  try {
    const extra = config().appState?.();
    return extra && typeof extra === 'object' ? extra : undefined;
  } catch {
    return undefined;
  }
}

/** Open the report: an unsent draft comes back as it was, otherwise a new one. */
export function open(): Promise<void> {
  // П316: off in the tracker — not even through the API.
  if (typeof document === 'undefined' || !bugReportOffered()) return Promise.resolve();
  if (opening) return opening;
  if (store.draft && !store.sent) {
    set({ minimized: false });
    return Promise.resolve();
  }
  return startDraft();
}

const sameQuestion = (a: QuestionSeed, b: QuestionSeed): boolean =>
  a.id !== null ? a.id === b.id : b.id === null && a.question === b.question && a.place === b.place;

/**
 * «Сделать репортом» from a chat of questions (Чт3, Quint ask_chat): a new draft headed by the places and answers of every
 * turn of the chat. An unsent draft keeps all it has (same dialog, same key) and gets the blocks it has not got yet; the
 * same question twice is one block.
 */
export function openFromQuestions(seeds: QuestionSeed[]): Promise<void> {
  if (typeof document === 'undefined' || seeds.length === 0) return Promise.resolve();
  if (opening) return opening.then(() => openFromQuestions(seeds));
  if (store.draft && !store.sent) {
    const have = store.questions;
    const added = seeds.filter((seed, index) => !have.some((item) => sameQuestion(item, seed)) && seeds.findIndex((other) => sameQuestion(other, seed)) === index);
    set({ questions: added.length > 0 ? [...have, ...added] : have, minimized: false });
    return Promise.resolve();
  }
  return startDraft(seeds);
}

/** One question of the help panel as a report (the chat of one turn). */
export const openFromQuestion = (seed: QuestionSeed): Promise<void> => openFromQuestions([seed]);

export function startUxWatch(): void {
  if (typeof document === 'undefined' || store.ux || !uxWatchOffered()) return;
  ensureRoot();
  void ready();
  if (!uxWatchActive()) startUxRecording();
  set({ ux: true });
}

export interface MountOptions {
  section?: 'reports' | 'ux';
  /** The tracker site itself (tracker.jaiora.me): «Пользователи» in the settings. */
  tracker?: boolean;
}

/** Host settings page: a React root inside the host element; the returned function unmounts it. */
export function mountSettings(el: Element, options: MountOptions = {}): () => void {
  if (!(el instanceof Element)) throw new Error('jaioraBugs.mountSettings: нужен элемент страницы');
  injectStyles();
  const hadClass = el.classList.contains('jaiora-bugs');
  el.classList.add('jaiora-bugs');
  const unmark = markJuiRoot(el as HTMLElement);
  const settingsRoot = createRoot(el);
  settingsRoot.render(options.section === 'ux' ? <UxSessions /> : <BugReportSettings tracker={options.tracker === true} />);
  let mounted = true;
  return () => {
    if (!mounted) return;
    mounted = false;
    settingsRoot.unmount();
    unmark();
    if (!hadClass) el.classList.remove('jaiora-bugs');
  };
}

/**
 * "Что нового" as a screen of its own on a host page: the notes and "Где это?" — from the service (`whatsNew.service`, what
 * the host's deploy published) or the host's own endpoint (`whatsNew.path`, works without the service). A click on a
 * note or a place opens exactly that place (`whatsNew.navigate`).
 */
export function mountWhatsNew(el: Element, options: WhatsNewScreenProps = {}): () => void {
  if (!(el instanceof Element)) throw new Error('jaioraBugs.mountWhatsNew: нужен элемент страницы');
  injectStyles();
  const hadClass = el.classList.contains('jaiora-bugs');
  el.classList.add('jaiora-bugs');
  const unmark = markJuiRoot(el as HTMLElement);
  const screenRoot = createRoot(el);
  screenRoot.render(<WhatsNewScreen {...options} />);
  let mounted = true;
  return () => {
    if (!mounted) return;
    mounted = false;
    screenRoot.unmount();
    unmark();
    if (!hadClass) el.classList.remove('jaiora-bugs');
  };
}
